<template>
  <SamMenu
    :options="menuOptions"
    :close-on-content-click="!multiple"
    class="sam-filter"
  >
    <template #activator="{ on, isMenuOpen, toggle }">
      <a
        :class="[`sam-filter__menu-activator`, { 'is-disabled': disabled }]"
        data-testid="sam-filter__menu-activator"
        v-on="disabled ? {} : on"
      >
        <SamChip
          ref="chip"
          sam-type="outlined"
          :label="label"
          :disabled="disabled"
          :active="isChipActive"
        >
          <template #append>
            <div class="d-flex middle-xs">
              <SamIcon
                v-if="hasClear"
                class="sam-filter__clear-icon"
                icon="interface-close"
                size="xs"
                @click.stop="clear"
              />

              <SamIcon
                :class="['sam-filter__expand-icon', { 'is-flipped': isMenuOpen }]"
                icon="arrow-chevron-down"
                size="sm"
                @click.stop="toggle"
              />
            </div>
          </template>
          <!-- No option selected -->
          <template v-if="multiple ? !selectedOption.length : !selectedOption">
            {{ label }}
          </template>
          <!-- Single option selected -->
          <template v-else-if="!multiple || (multiple && selectedOption.length === 1)">
            {{ multiple ? selectedOption[0].label : selectedOption.label }}
          </template>
          <!-- Multiple options selected -->
          <template v-else-if="multiple && selectedOption.length > 1">
            <div class="d-flex gap-1 items-center">
              <span>{{ label }}</span>
              <SamBadge
                :sam-style="disabled ? 'inactive' : 'tertiary'"
                :value="selectedOption.length"
              />
            </div>
          </template>
          <!-- Fallback -->
          <template v-else>
            {{ label }}
          </template>
        </SamChip>
      </a>
    </template>

    <SamSelectList
      v-model="selectedValue"
      data-testid="sam-filter__select-list"
      :items="items"
      :multiple="multiple"
      :has-select-all="hasSelectAll"
      v-bind="$attrs"
      @item-click="onItemSelection"
      v-on="$listeners"
    >
      <template #no-data>
        <div
          v-if="!$slots['no-data']"
          data-testid="sam-filter__no-data"
          class="sam-filter__no-data"
        >
          No data to show
        </div>
        <template v-else>
          <slot name="no-data" />
        </template>
      </template>
    </SamSelectList>
  </SamMenu>
</template>

<script>
import SamMenu from '../SamMenu/SamMenu.vue';
import SamChip from '../SamChip/SamChip.vue';
import SamIcon from '../SamIcon/SamIcon.vue';
import SamBadge from '../SamBadge/SamBadge.vue';
import { objectHasOwnProperties } from '../../../utils/utils';
import SamSelectList from '../SamSelectList/SamSelectList.vue';

export default {
  name: 'SamFilter',
  components: {
    SamSelectList,
    SamIcon,
    SamMenu,
    SamChip,
    SamBadge,
  },
  model: {
    event: 'change',
  },
  props: {
    /**
     * The options for the dropdown
     */
    items: {
      type: Array,
      required: true,
      default: () => [],
      validator: (val) => val.every?.((item) => objectHasOwnProperties(item, 'label', 'value')),
    },
    /**
     * The selection value
     */
    value: {
      type: [Number, String, Array],
      required: false,
      default: undefined,
    },
    /**
     * The SamMenu options to be applied
     */
    menuOptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    /**
     * If the selection is disabled
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If it allows multiple selection
     */
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The Chip label
     */
    label: {
      type: String,
      required: true,
    },
    /**
     * If it has select all option in the menu
     */
    hasSelectAll: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * If the option can be cleared
     */
    clearable: {
      type: Boolean,
      default: true,
      required: false,
    },
  },
  data() {
    return {
      selectedValue: this.value ?? this.getClearValue(),
    };
  },
  computed: {
    isChipActive() {
      return this.multiple ? !!this.selectedOption?.length : !!this.selectedOption;
    },
    selectedOption() {
      if (this.multiple) {
        return this.items.filter((item) => this.selectedValue.includes(item.value));
      }
      return this.items.find((item) => item.value === this.value);
    },
    hasClear() {
      return this.clearable && this.isChipActive;
    },
  },
  watch: {
    selectedValue: {
      handler(value) {
        this.$emit('change', value);
      },
      deep: true,
    },
    value: {
      handler(value) {
        this.selectedValue = value ?? this.getClearValue();
      },
      deep: true,
    },
  },
  methods: {
    getClearValue() {
      if (this.multiple) {
        return [];
      }
      return null;
    },
    onItemSelection(item) {
      this.$emit('item-selection', item);
    },

    clear() {
      this.selectedValue = this.getClearValue();
      this.$emit('clear');
      this.$refs.chip.$el.blur();
    },
  },
};
</script>

<style scoped lang="scss">
.sam-filter__menu-activator {
  display: block;

  .sam-filter__no-data {
    @extend .text--caption, .p-4;
    color: $neutral--60;
    font-style: italic;
    text-align: center;
  }

  .sam-filter__expand-icon {
    transition: rotate 150ms ease-in-out;

    &.is-flipped {
      rotate: -180deg;
    }
  }

  &.is-disabled {
    cursor: not-allowed;

    .sam-filter__expand-icon {
      color: $neutral--50;
    }
  }
}

.sam-filter::v-deep {
  .sam-filter__no-data {
    @extend .text--caption, .p-4;
    color: $neutral--60;
    font-style: italic;
    text-align: center;
  }
}
</style>
